import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { TeamKeyMode } from '@arrowise/shared';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface UpdateTeamKeyModeDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentKeyMode: TeamKeyMode;
    isOwner: boolean;
    onUpdate: (newMode: TeamKeyMode) => Promise<void>;
    trigger?: React.ReactNode;
}

export const UpdateTeamKeyModeDialog: React.FC<UpdateTeamKeyModeDialogProps> = ({
    open,
    onOpenChange,
    currentKeyMode,
    isOwner,
    onUpdate,
    trigger,
}) => {
    const [keyMode, setKeyMode] = useState<TeamKeyMode>(currentKeyMode);
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleConfirm = async () => {
        setError(null);
        if (keyMode === currentKeyMode) {
            onOpenChange(false);
            return;
        }
        try {
            setIsUpdating(true);
            await onUpdate(keyMode);
            onOpenChange(false);
        } catch (e: any) {
            setError(e?.message || 'Failed to update key mode');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { setError(null); setKeyMode(currentKeyMode); onOpenChange(o); }}>
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Change Team AI Key Mode</DialogTitle>
                    <DialogDescription>
                        Choose whether this team uses Arrowise default AI key or brings its own key (BYOK).
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="team-key-mode">AI Key Mode</Label>
                        <Select value={keyMode} onValueChange={(v) => setKeyMode(v as TeamKeyMode)}>
                            <SelectTrigger id="team-key-mode" className="cursor-pointer w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="default" className="cursor-pointer">Default (Arrowise)</SelectItem>
                                <SelectItem value="byok" className="cursor-pointer">BYOK (Bring Your Own Key)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {error && (
                        <div className="p-3 rounded-md border border-destructive/40 bg-destructive/5 text-sm">
                            <p className="text-destructive">
                                {error}
                                {isOwner ? (
                                    <>
                                        {' '}
                                        <Link to="/app/settings?tab=keys" className="underline inline-flex items-center gap-1">
                                            Configure AI key
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </>
                                ) : (
                                    <>. Please contact the team owner to add an AI key (Settings -{'>'} API Keys).</>
                                )}
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={handleConfirm} disabled={isUpdating}>{isUpdating ? 'Updating...' : 'Update'}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UpdateTeamKeyModeDialog;


